
import React, { useEffect } from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { fetchRestaurantFooter } from "../../actions/footerActions";

import {
  ArrowUpRight,
  Clock3,
  Leaf,
  Mail,
  MapPin,
  Phone,
  Heart,
} from "lucide-react";

import "./css/Footer.css";

function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const { restaurantId, tableId } = useParams();

  // Shared Redux API action; Theme 10 retains its own footer markup and CSS.
  const dispatch = useDispatch();
  const footerData = useSelector((state) => state.footer?.data || {});
  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchRestaurantFooter(restaurantId)).catch(() => {});
    }
  }, [dispatch, restaurantId]);
  const restaurantName = footerData.restaurant_name || "Restaurant";
  const description = footerData.description || "";
  const address = footerData.address || "";
  const phone = footerData.phone || "";
  const email = footerData.email || "";
  const openingHours = footerData.opening_hours || "";

  const currentYear = new Date().getFullYear();

  const homePath =
    `/${restaurantId}/table/${tableId}`;

  const isHomePage =
    location.pathname.replace(/\/+$/, "") === homePath;

  // HOME NAVIGATION
  const goHome = () => {
    if (isHomePage) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      return;
    }

    navigate(homePath);
  };

  // SECTION NAVIGATION
  const scrollToSection = (sectionId) => {
    if (isHomePage) {
      const element = document.getElementById(sectionId);

      if (element) {
        const headerHeight =
          document.querySelector(".theme10-header")
            ?.offsetHeight || 0;

        const top =
          element.getBoundingClientRect().top +
          window.scrollY -
          headerHeight -
          12;

        window.scrollTo({
          top,
          behavior: "smooth",
        });
      }

      return;
    }

    // Theme 10 Header handles the hash after
    // returning from Cart / Checkout / Payment.
    navigate(`${homePath}#${sectionId}`);
  };

  const exploreLinks = [
    {
      label: "Home",
      onClick: goHome,
    },
    {
      label: "Popular Dishes",
      onClick: () => scrollToSection("theme10-popular"),
    },
    {
      label: "About Us",
      onClick: () => scrollToSection("theme10-about"),
    },
    {
      label: "Our Menu",
      onClick: () => scrollToSection("theme10-menu"),
    },
  ];

  return (
    <footer
      className="theme10-footer"
      id="theme10-contact"
    >
      {/* DECORATIVE SHAPES */}
      <div className="theme10-footer-decoration theme10-footer-decoration-one" />
      <div className="theme10-footer-decoration theme10-footer-decoration-two" />

      <div className="theme10-footer-container">

        {/* MAIN FOOTER */}
        <div className="theme10-footer-main">

          {/* BRAND */}
          <div className="theme10-footer-brand">
            <button
              type="button"
              className="theme10-footer-logo"
              onClick={goHome}
              aria-label="Go to home"
            >
              <span className="theme10-footer-logo-icon">
                <Leaf size={20} />
              </span>

              <span className="theme10-footer-logo-name">
                {restaurantName}
              </span>
            </button>

            <p className="theme10-footer-description">
              {description}
            </p>

            <div className="theme10-footer-fresh">
              <Heart size={14} />
              <span>Freshly made with love</span>
            </div>
          </div>

          {/* EXPLORE */}
          <div className="theme10-footer-column">
            <h3>Explore</h3>

            {exploreLinks.map((link) => (
              <button
                key={link.label}
                type="button"
                className="theme10-footer-link"
                onClick={link.onClick}
              >
                <span>{link.label}</span>
                <ArrowUpRight size={14} />
              </button>
            ))}
          </div>

          {/* CONTACT — only actual backend data is displayed. */}
          <div className="theme10-footer-column">
            <h3>Contact</h3>
            {address && <div className="theme10-footer-contact-item">
              <span className="theme10-footer-contact-icon"><MapPin size={17} /></span>
              <p>{address}</p>
            </div>}
            {phone && <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="theme10-footer-contact-item">
              <span className="theme10-footer-contact-icon"><Phone size={16} /></span>
              <span>{phone}</span>
            </a>}
            {email && <a href={`mailto:${email}`} className="theme10-footer-contact-item">
              <span className="theme10-footer-contact-icon"><Mail size={16} /></span>
              <span>{email}</span>
            </a>}
          </div>

          {/* OPENING HOURS */}
          <div className="theme10-footer-column">
            <h3>Opening Hours</h3>

            {openingHours && <div className="theme10-footer-hours">
              <div className="theme10-footer-hours-row">
                <div className="theme10-footer-hours-title"><Clock3 size={15} /><span>{openingHours}</span></div>
              </div>
            </div>}

            <button
              type="button"
              className="theme10-footer-order-button"
              onClick={() => scrollToSection("theme10-menu")}
            >
              Explore Our Menu
              <ArrowUpRight size={15} />
            </button>
          </div>

        </div>

        <p className="theme10-footer-disclaimer">
          Food images are for presentation purposes only. Actual items may vary.
        </p>

        {/* DIVIDER */}
        <div className="theme10-footer-divider" />

        {/* BOTTOM FOOTER */}
        <div className="theme10-footer-bottom">
          <p>
            © {currentYear} {restaurantName}.
            All rights reserved.
          </p>

          <div className="theme10-footer-bottom-message">
            <Leaf size={13} />
            <span>
              Made with care, served with flavour
            </span>
          </div>

          <button
            type="button"
            className="theme10-footer-back-top"
            onClick={goHome}
          >
            Back to Top
            <ArrowUpRight size={14} />
          </button>
        </div>

      </div>
    
      {/* Restaurant social profiles (only show configured HTTPS/HTTP URLs) */}
      {[
        { field: "facebook_url", label: "Facebook", glyph: "f" },
        { field: "instagram_url", label: "Instagram", glyph: "◎" },
        { field: "youtube_url", label: "YouTube", glyph: "▶" },
        { field: "whatsapp_url", label: "WhatsApp", glyph: "◉" },
      ].some(({ field }) => /^https?:\/\//i.test((footerData?.[field] || "").trim())) && (
        <nav aria-label="Restaurant social media" style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: "14px", padding: "14px 18px" }}>
          {[
            { field: "facebook_url", label: "Facebook", glyph: "f" },
            { field: "instagram_url", label: "Instagram", glyph: "◎" },
            { field: "youtube_url", label: "YouTube", glyph: "▶" },
            { field: "whatsapp_url", label: "WhatsApp", glyph: "◉" },
          ].filter(({ field }) => /^https?:\/\//i.test((footerData?.[field] || "").trim())).map(({ field, label, glyph }) => (
            <a key={field} href={footerData[field].trim()} target="_blank" rel="noopener noreferrer" aria-label={label} title={label}
               style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: "32px", minHeight: "32px", color: "inherit", textDecoration: "none", fontWeight: 700, fontSize: "20px" }}>
              {glyph}
            </a>
          ))}
        </nav>
      )}
    </footer>
  );
}

export default Footer;